import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { Box, Center, Divider, Group, Loader, Stack, Text, Title } from '@mantine/core';

import { getReportDetails } from '@/api';
import { resolveGqlErrors } from '@/helpers';
import ErrorAlert from '@/components/ErrorAlert';

import ReportContent from './ReportContent';
import ConfigurationAlertView from './ConfigurationAlertView';

import classes from './report.module.css';

const ReportPage: React.FC = () => {
  const params = useParams<{ reportName: string }>();
  const reportName = decodeURIComponent(params.reportName ?? '');
  const { data, loading, error } = useQuery(getReportDetails, { variables: { reportName } });
  const [configurationAlertView, setConfigurationAlertView] = useState(false);

  const message = resolveGqlErrors(error || data?.getReportsConfigurations?.error);

  useEffect(() => {
    let isConnected = false;
    const detectReportStatus = (event: MessageEvent) => {
      if (typeof event.data !== 'string') {
        return;
      }

      if (event.data.indexOf('tableau.completed') >= 0 && !isConnected) {
        setConfigurationAlertView(true);
        return;
      }

      if (event.data.indexOf('tableau.listening') >= 0) {
        isConnected = true;
      }
    };

    window.addEventListener('message', detectReportStatus);

    return () => window.removeEventListener('message', detectReportStatus);
  }, []);

  if (loading) {
    return (
      <Center h="100dvh">
        <Loader type="dots" />
      </Center>
    );
  }

  if (message) {
    return <ErrorAlert title={message} />;
  }

  const [report] = data?.getReportsConfigurations?.reportsConfigurations ?? [];

  if (!report) {
    return <ErrorAlert title="Report not found" />;
  }

  return (
    <>
      <Title>{report.headline}</Title>
      <Divider my="sm" />
      {configurationAlertView ? (
        <ConfigurationAlertView />
      ) : (
        <>
          <Box className={classes.reportSubtitle}  dangerouslySetInnerHTML={{ __html: report.subtitle }} />
          <ReportContent
            desktopUrl={report.tableauDesktopUrl}
            mobileUrl={report.tableauMobileUrl}
          />
          <Text mt="xl" fz="xs">*This data is as of today 3 AM CST.</Text>
          <Divider mt="xl" />
          <Title order={4} tt="uppercase">Glossary</Title>
          <Stack gap="md" my="md">
            {(report.terms ?? []).map((term) => (
              <Text key={term.term}><Text fw="bold" span>{term.term} — </Text>{term.description}</Text>
            ))}
          </Stack>
        </>
      )}
    </>
  );
};

export default ReportPage;
